// ================================================================
// Firmenfarbe (org-branding) — EINE Quelle für alle Oberflächen.
// ================================================================
// Die Farbe gehört der Organisation, nicht dem Gerät und nicht dem Benutzer:
// sie steht in `Organization.theme` und kommt über /api/org-theme.
//
// Warum ein eigenes Modul: die Liste der Farben, ihre Punkte in der Auswahl und
// das Anwenden auf das DOM lagen bisher nur im Dashboard. Handy-App, Onboarding
// und Dashboard hätten sonst drei Kopien derselben Liste — die laufen erfahrungs-
// gemäss auseinander (eine Farbe wird ergänzt, die anderen zwei Stellen nicht).
//
// Angewendet wird als Attribut `data-org-theme` auf <html>. Bewusst dort und
// nicht auf einem Container: Sheets/Tooltips hängen als Portal an document.body
// und müssen die Tokens ebenfalls erreichen (gleiche Lektion wie bei --cd-*).
import { getOrgTheme, setOrgThemeApi } from './org.js';

// Muss mit ALLOWED_THEMES im Backend (orgTheme.routes.ts) und den CSS-Blöcken
// in company-dashboard.css / LeadMobileView.css übereinstimmen.
export const ORG_THEMES = ['violet', 'blue', 'green', 'rose'];

// Der Punkt in der Auswahl — die kräftige Grundfarbe des jeweiligen Schemas.
export const ORG_THEME_DOTS = {
  violet: '#8b5cf6',
  blue: '#3b82f6',
  green: '#10b981',
  rose: '#f43f5e',
};

export const DEFAULT_ORG_THEME = 'violet';

// Zuletzt bekannte Farbe im Browser. Ohne sie startet die App IMMER in der
// Grundfarbe und springt erst um, wenn /api/org-theme geantwortet hat — ein
// sichtbares Aufblitzen der falschen Farbe bei jedem Start.
// ACHTUNG: derselbe Schlüssel wird in core/auth.js (clearSession) beim Abmelden
// gelöscht — sonst begrüsst die nächste Anmeldung mit der Farbe der Vorfirma.
export const ORG_THEME_CACHE_KEY = 'lu.orgTheme';

export function isOrgTheme(theme) {
  return ORG_THEMES.includes(theme);
}

// Zuletzt bekannter Stand — damit eine Auswahl-Liste sofort etwas anzeigen kann,
// ohne auf die Antwort des Servers zu warten.
let currentTheme = DEFAULT_ORG_THEME;

export function getCurrentOrgTheme() {
  return currentTheme;
}

// Farbe aufs Dokument legen. Unbekannte Werte fallen auf den Standard zurück —
// nie ein Attribut setzen, für das es keinen CSS-Block gibt.
export function applyOrgTheme(theme) {
  currentTheme = isOrgTheme(theme) ? theme : DEFAULT_ORG_THEME;
  document.documentElement.setAttribute('data-org-theme', currentTheme);
  return currentTheme;
}

// Beim Start SOFORT (ohne Netz) die zuletzt bekannte Farbe anlegen.
export function applyCachedOrgTheme() {
  let cached = null;
  try { cached = localStorage.getItem(ORG_THEME_CACHE_KEY); } catch { /* Privat-Modus */ }
  return applyOrgTheme(cached);
}

// Beim Start der App aufrufen. Scheitert der Aufruf (offline, kein Recht), bleibt
// es beim zuletzt bekannten Stand — die App startet trotzdem.
export async function loadOrgTheme() {
  applyCachedOrgTheme();
  try {
    const data = await getOrgTheme();
    const theme = applyOrgTheme(data && data.theme);
    try { localStorage.setItem(ORG_THEME_CACHE_KEY, theme); } catch { /* Privat-Modus */ }
    return theme;
  } catch {
    return currentTheme;
  }
}

// Auswahl speichern. Erst optimistisch anwenden (die Farbe soll beim Tippen
// sofort umspringen), bei einem Fehler auf den vorherigen Stand zurück —
// nie eine Farbe zeigen, die in der Datenbank nicht steht.
export async function saveOrgTheme(theme) {
  if (!isOrgTheme(theme)) throw new Error('Unbekanntes Farbschema: ' + theme);
  const previous = currentTheme;
  applyOrgTheme(theme);
  try {
    const data = await setOrgThemeApi(theme);
    const saved = applyOrgTheme(data && data.theme ? data.theme : theme);
    try { localStorage.setItem(ORG_THEME_CACHE_KEY, saved); } catch { /* Privat-Modus */ }
    return saved;
  } catch (err) {
    applyOrgTheme(previous);
    throw err;
  }
}
